import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown, ChevronRight, Save, Download, User, FileSignature,
  Clock, History, Trash2, Plus as PlusIcon, Circle, ListChecks,
  CheckCircle2,
} from 'lucide-react';
import { API, headers } from './shared';

async function _downloadPdf(url, filename) {
  try {
    const res = await fetch(url, { headers: headers(false) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const dl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(dl);
    return true;
  } catch (e) {
    console.warn('PDF download failed:', e);
    return false;
  }
}

function GrievantFactSheetPanel({ caseId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/grievant-fact-sheet`,
                            { headers: headers(false) });
      const d = await r.json();
      if (d.ok) {
        setData(d.grievant_fact_sheet);
        setDraft(d.grievant_fact_sheet || {});
      }
    } catch { /* swallow */ }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/grievant-fact-sheet`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify(draft),
      });
      const d = await r.json();
      if (d.ok) setData(d.grievant_fact_sheet);
    } catch (e) { console.warn('save GFS failed', e); }
    setSaving(false);
  }

  const fields = [
    ["name",                "Name"],
    ["employee_id",         "Employee ID / Badge #"],
    ["job_title",           "Job Title / Classification"],
    ["department_location", "Department / Location"],
    ["hire_date",           "Hire Date (YYYY-MM-DD)"],
    ["seniority_date",      "Seniority Date (YYYY-MM-DD)"],
    ["union_status",        "Union Status"],
    ["discipline_history",  "Discipline History"],
  ];

  return (
    <div className="cl-section cl-gdoc" data-testid="grievant-fact-sheet-section">
      <button type="button" className="cl-step2-head" onClick={() => setOpen(o => !o)}
              data-testid="grievant-fact-sheet-toggle" aria-expanded={open}>
        <User size={14} />
        <span className="cl-step2-title">§8 Grievant Fact Sheet</span>
        <span className="cl-step2-count" data-testid="grievant-fact-sheet-status">
          {data?.name ? data.name : '— Empty'}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="cl-gdoc-body">
          <div className="cl-gdoc-grid">
            {fields.map(([k, label]) => (
              <label key={k} className="cl-gdoc-field" data-testid={`gfs-field-${k}`}>
                <span className="cl-gdoc-label">{label}</span>
                <input
                  type="text"
                  className="cl-gdoc-input"
                  value={draft[k] || ''}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  data-testid={`gfs-input-${k}`}
                />
              </label>
            ))}
          </div>
          <div className="cl-gdoc-actions">
            <button type="button" className="cl-gdoc-save" onClick={save}
                    disabled={saving} data-testid="gfs-save-btn">
              <Save size={14} />{saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="cl-gdoc-export" onClick={() =>
              _downloadPdf(`${API}/api/case-locker/cases/${caseId}/grievant-fact-sheet/pdf`,
                           `grievant_fact_sheet_${caseId}.pdf`)
            } data-testid="gfs-export-btn">
              <Download size={14} /> Export PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StewardFactSheetPanel({ caseId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/steward-fact-sheet`,
                            { headers: headers(false) });
      const d = await r.json();
      if (d.ok) {
        setData(d.steward_fact_sheet);
        setDraft(d.steward_fact_sheet || {});
      }
    } catch { /* swallow */ }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/steward-fact-sheet`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({
          ...draft,
          parties_present: typeof draft.parties_present === 'string'
            ? draft.parties_present.split('\n').map(s => s.trim()).filter(Boolean)
            : draft.parties_present,
          witnesses: typeof draft.witnesses === 'string'
            ? draft.witnesses.split('\n').map(s => s.trim()).filter(Boolean)
            : draft.witnesses,
        }),
      });
      const d = await r.json();
      if (d.ok) setData(d.steward_fact_sheet);
    } catch (e) { console.warn('save SFS failed', e); }
    setSaving(false);
  }

  const partiesStr = Array.isArray(draft.parties_present)
    ? draft.parties_present.join('\n') : (draft.parties_present || '');
  const witnessesStr = Array.isArray(draft.witnesses)
    ? draft.witnesses.join('\n') : (draft.witnesses || '');

  return (
    <div className="cl-section cl-gdoc" data-testid="steward-fact-sheet-section">
      <button type="button" className="cl-step2-head" onClick={() => setOpen(o => !o)}
              data-testid="steward-fact-sheet-toggle" aria-expanded={open}>
        <FileSignature size={14} />
        <span className="cl-step2-title">§9 Steward Fact Sheet</span>
        <span className="cl-step2-count" data-testid="steward-fact-sheet-status">
          {data?.steward_name ? data.steward_name : '— Empty'}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="cl-gdoc-body">
          <div className="cl-gdoc-grid">
            <label className="cl-gdoc-field" data-testid="sfs-field-steward_name">
              <span className="cl-gdoc-label">Steward Name</span>
              <input className="cl-gdoc-input" value={draft.steward_name || ''}
                onChange={e => setDraft({ ...draft, steward_name: e.target.value })}
                data-testid="sfs-input-steward_name" />
            </label>
            <label className="cl-gdoc-field" data-testid="sfs-field-local_and_position">
              <span className="cl-gdoc-label">Local &amp; Position</span>
              <input className="cl-gdoc-input" value={draft.local_and_position || ''}
                onChange={e => setDraft({ ...draft, local_and_position: e.target.value })}
                data-testid="sfs-input-local_and_position" />
            </label>
            <label className="cl-gdoc-field" data-testid="sfs-field-incident_date">
              <span className="cl-gdoc-label">Date of Incident</span>
              <input className="cl-gdoc-input" value={draft.incident_date || ''}
                onChange={e => setDraft({ ...draft, incident_date: e.target.value })}
                data-testid="sfs-input-incident_date" placeholder="YYYY-MM-DD" />
            </label>
            <label className="cl-gdoc-field" data-testid="sfs-field-grievant_interview_date">
              <span className="cl-gdoc-label">Date Interviewed Grievant</span>
              <input className="cl-gdoc-input" value={draft.grievant_interview_date || ''}
                onChange={e => setDraft({ ...draft, grievant_interview_date: e.target.value })}
                data-testid="sfs-input-grievant_interview_date" placeholder="YYYY-MM-DD" />
            </label>
            <label className="cl-gdoc-field cl-gdoc-field-full" data-testid="sfs-field-parties_present">
              <span className="cl-gdoc-label">Parties Present (one per line)</span>
              <textarea className="cl-gdoc-textarea" rows={2}
                value={partiesStr}
                onChange={e => setDraft({ ...draft, parties_present: e.target.value })}
                data-testid="sfs-input-parties_present" />
            </label>
            <label className="cl-gdoc-field cl-gdoc-field-full" data-testid="sfs-field-witnesses">
              <span className="cl-gdoc-label">Witnesses (one per line)</span>
              <textarea className="cl-gdoc-textarea" rows={2}
                value={witnessesStr}
                onChange={e => setDraft({ ...draft, witnesses: e.target.value })}
                data-testid="sfs-input-witnesses" />
            </label>
            <label className="cl-gdoc-field cl-gdoc-field-full" data-testid="sfs-field-narrative_summary">
              <span className="cl-gdoc-label">Steward&apos;s Narrative Summary</span>
              <textarea className="cl-gdoc-textarea" rows={4}
                value={draft.narrative_summary || ''}
                onChange={e => setDraft({ ...draft, narrative_summary: e.target.value })}
                data-testid="sfs-input-narrative_summary" />
            </label>
          </div>
          <div className="cl-gdoc-actions">
            <button type="button" className="cl-gdoc-save" onClick={save}
                    disabled={saving} data-testid="sfs-save-btn">
              <Save size={14} />{saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="cl-gdoc-export" onClick={() =>
              _downloadPdf(`${API}/api/case-locker/cases/${caseId}/steward-fact-sheet/pdf`,
                           `steward_fact_sheet_${caseId}.pdf`)
            } data-testid="sfs-export-btn">
              <Download size={14} /> Export PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SpecTimelinePanel({ caseId }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState({ date: '', event: '', participants: '', evidence: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/spec-timeline`,
                            { headers: headers(false) });
      const d = await r.json();
      if (d.ok) setEntries(d.spec_timeline || []);
    } catch { /* swallow */ }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!(draft.event || '').trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/spec-timeline`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          date: draft.date,
          event: draft.event,
          participants: draft.participants.split('\n').map(s => s.trim()).filter(Boolean),
          evidence: draft.evidence.split('\n').map(s => s.trim()).filter(Boolean),
        }),
      });
      const d = await r.json();
      if (d.ok) {
        setEntries(d.spec_timeline);
        setDraft({ date: '', event: '', participants: '', evidence: '' });
      }
    } catch (e) { console.warn('add entry failed', e); }
    setBusy(false);
  }

  async function remove(entryId) {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/case-locker/cases/${caseId}/spec-timeline/${entryId}`, {
        method: 'DELETE', headers: headers(false),
      });
      const d = await r.json();
      if (d.ok) setEntries(d.spec_timeline);
    } catch (e) { console.warn('delete entry failed', e); }
    setBusy(false);
  }

  return (
    <div className="cl-section cl-gdoc" data-testid="spec-timeline-section">
      <button type="button" className="cl-step2-head" onClick={() => setOpen(o => !o)}
              data-testid="spec-timeline-toggle" aria-expanded={open}>
        <History size={14} />
        <span className="cl-step2-title">§10 Timeline (Date / Event / Participants / Evidence)</span>
        <span className="cl-step2-count" data-testid="spec-timeline-count">
          {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="cl-gdoc-body">
          {entries.length === 0 ? (
            <p className="cl-empty-text">No timeline entries yet. Use the form below to add the first one.</p>
          ) : (
            <div className="cl-spec-table" data-testid="spec-timeline-list">
              <div className="cl-spec-row cl-spec-row-head">
                <div>DATE</div>
                <div>EVENT</div>
                <div>PARTICIPANTS</div>
                <div>EVIDENCE</div>
                <div></div>
              </div>
              {entries.map((e, i) => (
                <div className="cl-spec-row" key={e.entry_id}
                     data-testid={`spec-timeline-entry-${i}`}>
                  <div data-testid={`spec-timeline-date-${i}`}>{e.date || '—'}</div>
                  <div data-testid={`spec-timeline-event-${i}`}>{e.event}</div>
                  <div data-testid={`spec-timeline-participants-${i}`}>
                    {(e.participants || []).join('; ') || '—'}
                  </div>
                  <div data-testid={`spec-timeline-evidence-${i}`}>
                    {(e.evidence || []).join('; ') || '—'}
                  </div>
                  <button type="button" className="cl-spec-del" onClick={() => remove(e.entry_id)}
                          data-testid={`spec-timeline-delete-${i}`} disabled={busy}
                          aria-label="Delete entry">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="cl-spec-add" data-testid="spec-timeline-add-form">
            <div className="cl-spec-add-grid">
              <input className="cl-gdoc-input" placeholder="Date (YYYY-MM-DD)"
                value={draft.date} onChange={e => setDraft({ ...draft, date: e.target.value })}
                data-testid="spec-timeline-input-date"
                data-test-alias="spec-timeline-date-input" />
              <input className="cl-gdoc-input" placeholder="Event (what happened)"
                value={draft.event} onChange={e => setDraft({ ...draft, event: e.target.value })}
                data-testid="spec-timeline-input-event"
                data-test-alias="spec-timeline-event-input" />
              <textarea className="cl-gdoc-textarea" rows={2} placeholder="Participants (one per line)"
                value={draft.participants}
                onChange={e => setDraft({ ...draft, participants: e.target.value })}
                data-testid="spec-timeline-input-participants"
                data-test-alias="spec-timeline-participants-input" />
              <textarea className="cl-gdoc-textarea" rows={2} placeholder="Evidence (one per line)"
                value={draft.evidence}
                onChange={e => setDraft({ ...draft, evidence: e.target.value })}
                data-testid="spec-timeline-input-evidence"
                data-test-alias="spec-timeline-evidence-input" />
            </div>
            <div className="cl-gdoc-actions">
              <button type="button" className="cl-gdoc-save" onClick={add} disabled={busy}
                      data-testid="spec-timeline-add-btn">
                <PlusIcon size={14} /> Add Entry
              </button>
              <button type="button" className="cl-gdoc-export" onClick={() =>
                _downloadPdf(`${API}/api/case-locker/cases/${caseId}/spec-timeline/pdf`,
                             `timeline_${caseId}.pdf`)
              } data-testid="spec-timeline-export-btn">
                <Download size={14} /> Export PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────
// P76 — STEP 2 GRIEVANCE BOOK CHECKLIST PANEL
// ──────────────────────────────────────────────
function Step2ChecklistPanel({ caseId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/case-locker/cases/${caseId}/step2-checklist`, {
        headers: headers(false),
      });
      const d = await res.json();
      if (d.ok) setData(d);
    } catch { /* swallow */ }
    setLoading(false);
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  async function toggleOverride(key, currentlyComplete) {
    setBusyKey(key);
    try {
      const res = await fetch(
        `${API}/api/case-locker/cases/${caseId}/step2-checklist/override`,
        {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ key, complete: !currentlyComplete }),
        },
      );
      const d = await res.json();
      if (d.ok) setData(d);
    } catch (err) {
      console.warn('checklist override failed:', err);
    }
    setBusyKey(null);
  }

  if (loading) {
    return (
      <div className="cl-section" data-testid="step2-checklist-section">
        <h3 className="cl-section-title">
          <ListChecks size={14} /> Step 2 Grievance Book
        </h3>
        <p className="cl-empty-text">Loading…</p>
      </div>
    );
  }
  if (!data) return null;

  const barColor =
    data.percent === 100 ? '#22c55e' :
    data.percent >= 60   ? '#3b82f6' :
    data.percent >= 30   ? '#fbbf24' : '#64748b';

  return (
    <div className="cl-section cl-step2" data-testid="step2-checklist-section">
      <button
        type="button"
        className="cl-step2-head"
        onClick={() => setOpen(o => !o)}
        data-testid="step2-checklist-toggle"
        aria-expanded={open}
      >
        <ListChecks size={14} />
        <span className="cl-step2-title">Step 2 Grievance Book</span>
        <span className="cl-step2-count" data-testid="step2-checklist-count">
          {data.complete} / {data.total}
        </span>
        {data.ready && (
          <span className="cl-step2-ready" data-testid="step2-checklist-ready-pill">READY</span>
        )}
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      <div className="cl-step2-bar-track" data-testid="step2-checklist-progress">
        <div
          className="cl-step2-bar-fill"
          style={{ width: `${data.percent}%`, background: barColor }}
        />
        <span className="cl-step2-bar-label">{data.percent}% complete</span>
      </div>

      {open && (
        <ul className="cl-step2-list" data-testid="step2-checklist-list">
          {data.items.map((it, i) => {
            const done = it.status === 'complete';
            return (
              <li
                key={it.key}
                className={`cl-step2-row ${done ? 'cl-step2-row-done' : ''}`}
                data-testid={`step2-checklist-item-${it.key}`}
                data-status={it.status}
              >
                <button
                  type="button"
                  className="cl-step2-check"
                  onClick={() => toggleOverride(it.key, done)}
                  disabled={busyKey === it.key}
                  data-testid={`step2-checklist-toggle-${it.key}`}
                  aria-label={`Toggle ${it.label}`}
                >
                  {done
                    ? <CheckCircle2 size={16} className="cl-step2-check-on" />
                    : <Circle size={16} className="cl-step2-check-off" />}
                </button>
                <div className="cl-step2-content">
                  <div className="cl-step2-label" data-testid={`step2-checklist-label-${it.key}`}>
                    {i + 1}. {it.label}
                  </div>
                  <div className="cl-step2-evidence" data-testid={`step2-checklist-evidence-${it.key}`}>
                    {it.evidence}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}



export {
  GrievantFactSheetPanel,
  StewardFactSheetPanel,
  SpecTimelinePanel,
  Step2ChecklistPanel,
};
